import {
  ONBOARDING_ACTIVITY_LABELS,
  ONBOARDING_DATA_SOURCE_LABELS,
  ONBOARDING_FREQUENCY_LABELS,
  ONBOARDING_GOAL_LABELS,
  ONBOARDING_HEALTH_SYNC_LABELS,
  ONBOARDING_LEVEL_LABELS,
  ONBOARDING_MOTIVATION_LABELS,
  ONBOARDING_PRIVACY_LABELS,
} from '../onboardingLabels';

export const PROFILE_DETAIL_OPTIONS = {
  gender: [
    { value: 'woman', label: 'Woman' },
    { value: 'man', label: 'Man' },
    { value: 'non_binary', label: 'Non-binary' },
    { value: 'prefer_not_to_say', label: 'Private' },
  ],
  goal: [
    { value: 'consistency', label: 'Build consistency' },
    { value: 'strength', label: 'Get stronger' },
    { value: 'build_muscle', label: 'Build muscle' },
    { value: 'endurance', label: 'Improve endurance' },
    { value: 'wellbeing', label: 'Feel healthier' },
  ],
  level: [
    { value: 'beginner', label: 'Beginner' },
    { value: 'moderate', label: 'Intermediate' },
    { value: 'fit', label: 'Advanced' },
    { value: 'athlete', label: 'Very active' },
  ],
  limitation: [
    { value: 'none', label: 'None' },
    { value: 'knees', label: 'Knees' },
    { value: 'lower_back', label: 'Lower back' },
    { value: 'shoulders', label: 'Shoulders' },
    { value: 'wrists', label: 'Wrists' },
  ],
  trainingLocation: [
    { value: 'gym', label: 'Gym' },
    { value: 'home', label: 'Home' },
    { value: 'outdoor', label: 'Outdoor' },
    { value: 'mixed', label: 'Mixed' },
  ],
};

export const PROFILE_DETAIL_COACH_FIELDS = [
  'gender',
  'age',
  'heightCm',
  'weightKg',
  'targetWeightKg',
  'goal',
  'selectedCoachGoal',
  'level',
  'avoidAreas',
  'trainingLocation',
  'equipmentAccess',
  'frequency',
  'frequencyDaysPerWeek',
  'activities',
];

const GOAL_TO_COACH_GOAL = {
  build_muscle: 'Build muscle',
  consistency: 'Build consistency',
  endurance: 'Improve endurance',
  strength: 'Get stronger',
  wellbeing: 'Feel healthier',
};

const FREQUENCY_TO_DAYS = {
  '2_3': 3,
  '4_5': 4,
  daily: 6,
  flexible: 3,
};

const LOCATION_TO_EQUIPMENT = {
  gym: ['dumbbell', 'barbell', 'machine'],
  home: ['bodyweight', 'dumbbell'],
  outdoor: ['bodyweight', 'no_gym'],
  mixed: ['bodyweight', 'dumbbell', 'machine'],
};

function optionLabel(options, value, fallback = 'Not set') {
  return options.find(option => option.value === value)?.label || fallback;
}

function numberOrBlank(value) {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? String(Math.round(number)) : '';
}

function cleanPositiveNumber(value, fallback = null) {
  const number = Number(value);
  if (!Number.isFinite(number) || number <= 0) return fallback;
  return Math.round(number);
}

function cleanString(value, fallback = '') {
  return String(value || fallback).trim();
}

function cleanArray(value) {
  return Array.isArray(value) ? value.filter(Boolean) : [];
}

function firstNameFromProfile(profile = {}) {
  const explicit = cleanString(profile.firstName || profile.onboarding?.firstName);
  if (explicit) return explicit;
  const name = cleanString(profile.displayName || profile.email?.split('@')[0] || 'Tribe member');
  return name.split(/\s+/)[0] || 'Tribe member';
}

export function readStoredProfileDetailsSetup() {
  if (typeof localStorage === 'undefined') return {};
  try {
    return JSON.parse(localStorage.getItem('tribelogWorkoutCoachSetup') || '{}');
  } catch (error) {
    return {};
  }
}

export function buildProfileDetailsDraft(profile = {}, storedSetup = readStoredProfileDetailsSetup()) {
  const onboarding = profile?.onboarding || {};
  const goal = storedSetup.goal || onboarding.goal || 'consistency';
  const frequency = storedSetup.frequency || onboarding.frequency || '4_5';
  const trainingLocation = storedSetup.trainingLocation
    || onboarding.trainingLocation
    || (cleanArray(storedSetup.equipmentAccess).includes('no_gym') ? 'home' : 'gym');
  const avoidAreas = cleanArray(storedSetup.avoidAreas?.length ? storedSetup.avoidAreas : onboarding.avoidAreas);
  const targetWeightKg = storedSetup.targetWeightKg || onboarding.targetWeightKg || '';

  return {
    activities: cleanArray(storedSetup.activities?.length ? storedSetup.activities : onboarding.activities),
    age: numberOrBlank(storedSetup.age || onboarding.age),
    avoidAreas: avoidAreas.length ? avoidAreas : ['none'],
    dataSource: storedSetup.dataSource || onboarding.dataSource || 'manual',
    firstName: firstNameFromProfile(profile),
    frequency,
    frequencyDaysPerWeek: cleanPositiveNumber(
      storedSetup.frequencyDaysPerWeek || onboarding.frequencyDaysPerWeek,
      FREQUENCY_TO_DAYS[frequency] || 4,
    ),
    gender: storedSetup.gender || onboarding.gender || 'prefer_not_to_say',
    goal,
    healthSync: storedSetup.healthSync || onboarding.healthSync || 'manual_first',
    heightCm: numberOrBlank(storedSetup.heightCm || onboarding.heightCm || 170),
    heightUnit: storedSetup.heightUnit || onboarding.heightUnit || 'cm',
    level: storedSetup.level || onboarding.level || 'beginner',
    motivation: storedSetup.motivation || onboarding.motivation || 'progress',
    privacy: storedSetup.privacy || onboarding.privacy || 'manual_first',
    selectedCoachGoal: storedSetup.selectedCoachGoal || onboarding.selectedCoachGoal || GOAL_TO_COACH_GOAL[goal] || 'Build consistency',
    sleepSyncInterest: storedSetup.sleepSyncInterest || onboarding.sleepSyncInterest || 'later',
    targetWeightKg: numberOrBlank(targetWeightKg),
    trainingLocation,
    weightKg: numberOrBlank(storedSetup.weightKg || onboarding.weightKg || 75),
    weightUnit: storedSetup.weightUnit || onboarding.weightUnit || 'kg',
  };
}

export function normalizeProfileDetailsDraft(draft = {}) {
  const goal = PROFILE_DETAIL_OPTIONS.goal.some(option => option.value === draft.goal)
    ? draft.goal
    : 'consistency';
  const trainingLocation = PROFILE_DETAIL_OPTIONS.trainingLocation.some(option => option.value === draft.trainingLocation)
    ? draft.trainingLocation
    : 'gym';
  const avoidAreas = cleanArray(draft.avoidAreas).filter(area =>
    PROFILE_DETAIL_OPTIONS.limitation.some(option => option.value === area)
  );
  const normalizedAvoidAreas = avoidAreas.length ? avoidAreas : ['none'];
  const frequency = draft.frequency || '4_5';
  const equipmentAccess = LOCATION_TO_EQUIPMENT[trainingLocation] || LOCATION_TO_EQUIPMENT.gym;
  const frequencyDaysPerWeek = cleanPositiveNumber(
    draft.frequencyDaysPerWeek,
    FREQUENCY_TO_DAYS[frequency] || 4,
  );

  return {
    activities: cleanArray(draft.activities),
    age: cleanPositiveNumber(draft.age),
    avoidAreas: normalizedAvoidAreas.includes('none') ? ['none'] : normalizedAvoidAreas,
    dataSource: draft.dataSource || 'manual',
    firstName: cleanString(draft.firstName, 'Tribe member').slice(0, 40),
    frequency,
    frequencyDaysPerWeek,
    gender: PROFILE_DETAIL_OPTIONS.gender.some(option => option.value === draft.gender)
      ? draft.gender
      : 'prefer_not_to_say',
    goal,
    healthSync: draft.healthSync || 'manual_first',
    heightCm: cleanPositiveNumber(draft.heightCm, 170),
    heightUnit: draft.heightUnit === 'ft' ? 'ft' : 'cm',
    level: PROFILE_DETAIL_OPTIONS.level.some(option => option.value === draft.level)
      ? draft.level
      : 'beginner',
    motivation: draft.motivation || 'progress',
    privacy: draft.privacy || 'manual_first',
    selectedCoachGoal: cleanString(draft.selectedCoachGoal, GOAL_TO_COACH_GOAL[goal] || 'Build consistency'),
    sleepSyncInterest: draft.sleepSyncInterest || 'later',
    targetWeightKg: cleanPositiveNumber(draft.targetWeightKg),
    trainingLocation,
    equipmentAccess,
    weightKg: cleanPositiveNumber(draft.weightKg, 75),
    weightUnit: draft.weightUnit === 'lb' ? 'lb' : 'kg',
  };
}

export function writeStoredProfileDetailsSetup(details = {}) {
  const setup = normalizeProfileDetailsDraft(details);
  localStorage.setItem('tribelogWorkoutCoachSetup', JSON.stringify(setup));
  if (setup.selectedCoachGoal) localStorage.setItem('tribelogWorkoutCoachGoal', setup.selectedCoachGoal);
  if (setup.frequencyDaysPerWeek) localStorage.setItem('tribelogWorkoutCoachDays', String(setup.frequencyDaysPerWeek));
  if (setup.gender) localStorage.setItem('tribelogWorkoutCoachGender', setup.gender);
  if (setup.age) localStorage.setItem('tribelogWorkoutCoachAge', String(setup.age));
  if (setup.goal) localStorage.setItem('tribelogWorkoutCoachMotive', setup.goal === 'build_muscle' ? 'muscle_gain' : setup.goal);
  return setup;
}

export function profileDetailsChangedForCoach(previous = {}, next = {}) {
  const before = normalizeProfileDetailsDraft(previous);
  const after = normalizeProfileDetailsDraft(next);
  return PROFILE_DETAIL_COACH_FIELDS.some(field => (
    JSON.stringify(before[field] ?? null) !== JSON.stringify(after[field] ?? null)
  ));
}

export function profileDetailsRows(details = {}) {
  const normalized = normalizeProfileDetailsDraft(details);
  const limitationLabel = normalized.avoidAreas.includes('none')
    ? 'None'
    : normalized.avoidAreas.map(value => optionLabel(PROFILE_DETAIL_OPTIONS.limitation, value, value)).join(', ');

  return [
    { field: 'firstName', label: 'First Name', value: normalized.firstName },
    { field: 'gender', label: 'Gender', value: optionLabel(PROFILE_DETAIL_OPTIONS.gender, normalized.gender, 'Private') },
    { field: 'age', label: 'Age', value: normalized.age ? `${normalized.age} years` : 'Not set' },
    { field: 'heightCm', label: 'Height', value: normalized.heightCm ? `${normalized.heightCm} cm` : 'Not set' },
    { field: 'weightKg', label: 'Current Weight', value: normalized.weightKg ? `${normalized.weightKg} kg` : 'Not set' },
    { field: 'goal', label: 'Fitness Goal', value: optionLabel(PROFILE_DETAIL_OPTIONS.goal, normalized.goal) },
    { field: 'targetWeightKg', label: 'Target Weight', value: normalized.targetWeightKg ? `${normalized.targetWeightKg} kg` : 'Optional' },
    { field: 'level', label: 'Training Experience', value: optionLabel(PROFILE_DETAIL_OPTIONS.level, normalized.level) },
    { field: 'avoidAreas', label: 'Limitations', value: limitationLabel },
    { field: 'trainingLocation', label: 'Training Location', value: optionLabel(PROFILE_DETAIL_OPTIONS.trainingLocation, normalized.trainingLocation) },
  ];
}

export function profileDetailsSourceRows(details = {}) {
  const normalized = normalizeProfileDetailsDraft(details);
  const activities = normalized.activities.map(id => ONBOARDING_ACTIVITY_LABELS[id]).filter(Boolean).join(', ');
  return [
    { label: 'ACTIVITIES', value: activities || 'Not set' },
    { label: 'RHYTHM', value: ONBOARDING_FREQUENCY_LABELS[normalized.frequency] || `${normalized.frequencyDaysPerWeek} days/week` },
    { label: 'MOTIVATION', value: ONBOARDING_MOTIVATION_LABELS[normalized.motivation] || 'Progress' },
    { label: 'DATA SOURCE', value: ONBOARDING_DATA_SOURCE_LABELS[normalized.dataSource] || 'Manual Logging' },
    { label: 'SYNC', value: ONBOARDING_HEALTH_SYNC_LABELS[normalized.healthSync] || 'Manual Logging First' },
    { label: 'PRIVACY', value: ONBOARDING_PRIVACY_LABELS[normalized.privacy] || 'Manual Logging First' },
    { label: 'ONBOARDING GOAL', value: ONBOARDING_GOAL_LABELS[normalized.goal] || optionLabel(PROFILE_DETAIL_OPTIONS.goal, normalized.goal) },
    { label: 'ONBOARDING LEVEL', value: ONBOARDING_LEVEL_LABELS[normalized.level] || optionLabel(PROFILE_DETAIL_OPTIONS.level, normalized.level) },
  ];
}
